import { ref, computed, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { removeInitialLoading } from '@/utils/loadingScreen'

const USER_Q_TELEGRAM = gql`
  query GetUserByTelegram($uid: BigInt!) {
    userByTelegram(telegramUserId: $uid) {
      id
      telegramUserId
      name
      username
      preferredTimezone
      preferredLanguage
      preferredDashboardPresentation
      boardsEnabled
      premiumActive
    }
  }
`

const USER_Q_CURRENT = gql`
  query GetCurrentUser {
    currentUser {
      id
      telegramUserId
      name
      username
      preferredTimezone
      preferredLanguage
      preferredDashboardPresentation
      boardsEnabled
      premiumActive
    }
  }
`

/**
 * Composable for fetching current user with automatic query selection
 * based on Telegram context or browser authentication.
 *
 * @param {Object} options - Configuration options
 * @param {string|null} options.fromId - Telegram user ID from props (optional)
 * @param {boolean} options.removeLoadingOnComplete - Whether to remove initial loading screen when auth completes (default: true)
 * @param {string} options.fetchPolicy - Apollo fetch policy (default: 'cache-and-network')
 * @returns {Object} - { currentUser, loading, error, refetch, userId, userTimezone, userLocale, boardsEnabled, premiumActive }
 */
export function useCurrentUser(options = {}) {
  const {
    fromId = null,
    removeLoadingOnComplete = true,
    fetchPolicy = 'cache-and-network',
  } = options

  // Detect Telegram context
  const viewerUid = computed(() => {
    const tg =
      typeof window !== 'undefined' &&
      window.Telegram &&
      window.Telegram.WebApp &&
      window.Telegram.WebApp.initDataUnsafe
    return (tg && tg.user && tg.user.id) || fromId || null
  })

  // Use currentUser query when there's no Telegram context (browser authentication)
  // Otherwise use userByTelegram query
  const useCurrentUserQuery = computed(() => !viewerUid.value)

  // User data refs
  const userId = ref(null)
  const userTimezone = ref(null)
  const userLocale = ref(null)
  const boardsEnabled = ref(true) // Default to true, will be updated from query
  const premiumActive = ref(false)

  // Telegram query
  const {
    result: userResultTelegram,
    loading: userLoadingTelegram,
    error: userErrorTelegram,
    refetch: refetchUserTelegram,
  } = useQuery(USER_Q_TELEGRAM, () => ({ uid: Number(viewerUid.value) }), {
    enabled: computed(() => !!viewerUid.value),
    skip: useCurrentUserQuery.value,
    fetchPolicy,
  })

  // Browser auth query
  const {
    result: userResultCurrent,
    loading: userLoadingCurrent,
    error: userErrorCurrent,
    refetch: refetchUserCurrent,
  } = useQuery(
    USER_Q_CURRENT,
    {},
    {
      enabled: useCurrentUserQuery,
      skip: !useCurrentUserQuery.value,
      fetchPolicy,
    }
  )

  // Combine results - use whichever query is active
  const userResult = computed(() =>
    useCurrentUserQuery.value ? userResultCurrent.value : userResultTelegram.value
  )
  const loading = computed(() =>
    useCurrentUserQuery.value ? userLoadingCurrent.value : userLoadingTelegram.value
  )
  const error = computed(() =>
    useCurrentUserQuery.value ? userErrorCurrent.value : userErrorTelegram.value
  )

  // Current user
  const currentUser = computed(() => {
    const userData = userResult.value?.userByTelegram || userResult.value?.currentUser
    return userData || null
  })

  // Helper function to refetch user data
  const refetch = async () => {
    if (useCurrentUserQuery.value) {
      await refetchUserCurrent()
    } else {
      await refetchUserTelegram({ uid: Number(viewerUid.value) })
    }
  }

  // Watch for user data and extract properties
  watch(
    userResult,
    data => {
      const user = data?.userByTelegram || data?.currentUser
      if (user) {
        userId.value = user.id
        // Use preferredTimezone or fall back to browser timezone, then UTC
        userTimezone.value =
          user.preferredTimezone ||
          (typeof Intl !== 'undefined' && Intl.DateTimeFormat
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null) ||
          'UTC'
        // Use preferredLanguage or fall back to browser language
        const preferredLang =
          user.preferredLanguage ||
          (typeof navigator !== 'undefined' && navigator.language
            ? navigator.language.substring(0, 2)
            : 'en')
        userLocale.value = preferredLang === 'ru' ? 'ru' : 'en'
        boardsEnabled.value = user.boardsEnabled !== undefined ? user.boardsEnabled : true
        premiumActive.value = user.premiumActive === true
      } else {
        // Reset values if no user
        userId.value = null
        userTimezone.value = null
        userLocale.value = null
        boardsEnabled.value = true
        premiumActive.value = false
      }
    },
    { immediate: true }
  )

  // Remove HTML loading screen when authentication is complete
  if (removeLoadingOnComplete) {
    watch([loading, currentUser], ([isLoading, user]) => {
      // Remove HTML loading when:
      // 1. Not loading anymore (authentication check complete)
      // 2. Either we have a user (show content) or no user (show welcome screen)
      if (!isLoading) {
        removeInitialLoading()
      }
    })
  }

  return {
    currentUser,
    loading,
    error,
    refetch,
    userId,
    userTimezone,
    userLocale,
    boardsEnabled,
    premiumActive,
  }
}
